import { calculateDistance } from "./calculateDistance";
import { DBSCAN } from "density-clustering"; // Import DBSCAN

/**
 * Removes pauses from a GPX track and returns the cleaned track along with details of the removed pauses and statistics.
 * @param {Array<object>} trackPoints - The array of GPX points.
 * @param {object} options - Configuration options.
 * @param {number} [options.speedThreshold=0.75] - The speed below which movement is considered a pause (m/s).
 * @param {number} [options.minPauseDuration=1] - The minimum duration for a pause to be removed (seconds).
 * @param {number} [options.radius=null] - Optional: Radius for clustering stops (in meters). If provided, stops within this radius will be clustered.
 * @returns {{cleanedTrack: Array<object>, stops: Array<{lat: number, lng: number, duration: number, distance: number, start?: string, end?: string, isCluster?: boolean, originalStopCount?: number}>, statistics: {duration: number, distance: number, stopsDuration: number, durationMin: number, durationMax: number}}} An object containing the cleaned track, an array of detected stops, and statistics.
 */
export const calculateTrackMotion = (trackPoints, options = {}) => {
  // --- 1. Set default options and handle edge cases ---
  const {
    speedThreshold = 0.75,
    minPauseDuration = 1,
    radius = null,
  } = options;
  if (trackPoints.length < 2) {
    return {
      cleanedTrack: trackPoints,
      stops: [],
      statistics: {
        duration: 0,
        distance: 0,
        stopsDuration: 0,
        durationMin: 0,
        durationMax: 0,
      },
    };
  }

  // --- 2. Initialize variables ---
  const cleanedTrack = [trackPoints[0]];
  const stops = []; // Array to store information about removed stops
  let pauseBuffer = [];
  let totalDistance = 0; // Track total distance in meters
  let totalPauseDuration = 0; // Track total duration of removed stops in seconds
  let minStopDuration = Infinity; // Initialize min stop duration
  let maxStopDuration = -Infinity; // Initialize max stop duration

  // --- 3. Iterate through points to identify and filter stops ---
  for (let i = 1; i < trackPoints.length; i++) {
    const prevPoint = trackPoints[i - 1];
    const currentPoint = trackPoints[i];

    const distance = calculateDistance(
      prevPoint.lat,
      prevPoint.lng,
      currentPoint.lat,
      currentPoint.lng
    );
    const timeDiff =
      (new Date(currentPoint.time) - new Date(prevPoint.time)) / 1000;
    const speed = timeDiff > 0 ? distance / timeDiff : 0;

    if (speed < speedThreshold) {
      pauseBuffer.push(currentPoint);
    } else {
      if (pauseBuffer.length > 0) {
        const pauseStartPoint = cleanedTrack[cleanedTrack.length - 1];
        const pauseEndPoint = pauseBuffer[pauseBuffer.length - 1];
        const pauseDuration =
          (new Date(pauseEndPoint.time) - new Date(pauseStartPoint.time)) /
          1000;

        if (pauseDuration >= minPauseDuration) {
          // Pause is long enough to be removed, so log its details and add to total pause duration
          const roundedPauseDuration = Math.round(pauseDuration);
          stops.push({
            lat: pauseStartPoint.lat,
            lng: pauseStartPoint.lng,
            duration: roundedPauseDuration,
            start: pauseEndPoint.time,
            end: pauseEndPoint.time,
            distance: Math.round(totalDistance),
          });
          totalPauseDuration += pauseDuration;
          minStopDuration = Math.min(minStopDuration, roundedPauseDuration);
          maxStopDuration = Math.max(maxStopDuration, roundedPauseDuration);
        } else {
          // Pause was too short, so keep the points and add distance
          cleanedTrack.push(...pauseBuffer);
          for (let j = 1; j < pauseBuffer.length; j++) {
            totalDistance += calculateDistance(
              pauseBuffer[j - 1].lat,
              pauseBuffer[j - 1].lng,
              pauseBuffer[j].lat,
              pauseBuffer[j].lng
            );
          }
          totalDistance += distance; // Add distance to current point
        }
        pauseBuffer = [];
      } else {
        totalDistance += distance; // Add distance to current point
      }
      cleanedTrack.push(currentPoint);
    }
  }

  // --- 4. Final check if the track ends during a pause ---
  if (pauseBuffer.length > 0) {
    const pauseStartPoint = cleanedTrack[cleanedTrack.length - 1];
    const pauseEndPoint = pauseBuffer[pauseBuffer.length - 1];
    const pauseDuration =
      (new Date(pauseEndPoint.time) - new Date(pauseStartPoint.time)) / 1000;

    if (pauseDuration >= minPauseDuration) {
      const roundedPauseDuration = Math.round(pauseDuration);
      stops.push({
        lat: pauseStartPoint.lat,
        lng: pauseStartPoint.lng,
        duration: roundedPauseDuration,
        distance: Math.round(totalDistance),
      });
      totalPauseDuration += pauseDuration;
      minStopDuration = Math.min(minStopDuration, roundedPauseDuration);
      maxStopDuration = Math.max(maxStopDuration, roundedPauseDuration);
    } else {
      cleanedTrack.push(...pauseBuffer);
      for (let j = 1; j < pauseBuffer.length; j++) {
        totalDistance += calculateDistance(
          pauseBuffer[j - 1].lat,
          pauseBuffer[j - 1].lng,
          pauseBuffer[j].lat,
          pauseBuffer[j].lng
        );
      }
    }
  }

  let processedStops = [...stops]; // Use a copy of stops for processing

  if (radius !== null) {
    const dbscan = new DBSCAN();
    const data = stops.map((stop) => [stop.lat, stop.lng]);
    const metersPerDegree = 111139; // Approximate conversion factor for latitude
    const eps = radius / metersPerDegree; // Convert radius from meters to degrees

    const clusters = dbscan.run(data, eps, 2); // eps (radius in degrees), minPts (minimum points in a cluster)

    const clusteredStops = [];
    const usedStopIndices = new Set();

    clusters.forEach((cluster) => {
      if (cluster.length > 0) {
        let clusterLat = 0;
        let clusterLng = 0;
        let clusterDuration = 0;
        let firstStopDistance = null; // To store the distance of the first stop in the cluster
        let clusterStart = null;
        let clusterEnd = null;

        cluster.forEach((index) => {
          const stop = stops[index];
          clusterLat += stop.lat;
          clusterLng += stop.lng;
          clusterDuration += stop.duration;
          if (firstStopDistance === null || stop.distance < firstStopDistance) {
            firstStopDistance = stop.distance;
          }
          if (
            !clusterStart ||
            (stop.start && new Date(stop.start) < new Date(clusterStart))
          ) {
            clusterStart = stop.start;
          }
          if (
            !clusterEnd ||
            (stop.end && new Date(stop.end) > new Date(clusterEnd))
          ) {
            clusterEnd = stop.end;
          }
          usedStopIndices.add(index);
        });

        clusteredStops.push({
          lat: clusterLat / cluster.length,
          lng: clusterLng / cluster.length,
          duration: Math.round(clusterDuration),
          distance: Math.round(firstStopDistance), // Use the distance of the first stop in the cluster
          start: clusterStart,
          end: clusterEnd,
          isCluster: true, // Mark as a clustered stop
          originalStopCount: cluster.length,
        });
      }
    });

    // Add any noise points (stops not part of any cluster)
    stops.forEach((stop, index) => {
      if (!usedStopIndices.has(index)) {
        clusteredStops.push(stop);
      }
    });

    processedStops = clusteredStops;

    // Recalculate totalPauseDuration, minStopDuration, maxStopDuration based on clustered stops
    totalPauseDuration = processedStops.reduce(
      (sum, stop) => sum + stop.duration,
      0
    );
    minStopDuration = processedStops.reduce(
      (min, stop) => Math.min(min, stop.duration),
      Infinity
    );
    maxStopDuration = processedStops.reduce(
      (max, stop) => Math.max(max, stop.duration),
      -Infinity
    );
  }

  // Sort processedStops by distance
  processedStops.sort((a, b) => a.distance - b.distance);

  const totalStopDistance = processedStops.reduce((accumulator, stop) => {
    return accumulator + stop.distance;
  }, 0);

  // --- 5. Calculate total duration, excluding removed stops ---
  const totalDuration =
    cleanedTrack.length > 1
      ? (new Date(cleanedTrack[cleanedTrack.length - 1].time) -
          new Date(cleanedTrack[0].time)) /
          1000 -
        totalPauseDuration
      : 0;

  return {
    cleanedTrack,
    stops: processedStops, // Return processedStops (clustered or original)
    statistics: {
      duration: Math.round(totalDuration), // Duration in seconds, excluding removed stops
      distance: Math.round(totalDistance), // Distance in meters
      speed:
        totalDuration > 0
          ? parseFloat(((totalDistance / totalDuration) * 3.6).toFixed(1))
          : 0,
      pace:
        totalDuration > 0
          ? parseFloat((totalDuration / 60 / (totalDistance / 1000)).toFixed(1))
          : 0,
      stopsDuration: Math.round(totalPauseDuration), // Total duration of removed stops in seconds
      durationMin: minStopDuration === Infinity ? 0 : minStopDuration,
      durationMax: maxStopDuration === -Infinity ? 0 : maxStopDuration,
    },
  };
};
